// Shared Indian-locale number formatting helpers.
//
// Use these for any rupee-value text input across the app:
//   <input type="text" inputMode="decimal"
//          value={formatINR(currentVal)}
//          onChange={(e) => onChange(stripCommas(e.target.value))} />
//
// Display rule:
//   • Whole-rupee amounts (|n| >= 100) render in Indian numbering — 1,75,81,260
//   • Ratios / percentages / small numbers (|n| < 100) keep up to 4 decimals
//   • null / undefined / "" → ""

/**
 * Format a numeric value for display. Returns an Indian-locale-grouped
 * string for whole-rupee amounts; preserves precision for ratios.
 */
export const formatINR = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const str = typeof v === 'string' ? v : String(v);
  const cleaned = str.replace(/,/g, '');
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return String(v);

  if (Math.abs(n) >= 100 || (Math.abs(n) > 0 && cleaned.includes('.'))) {
    // If input has a decimal portion, preserve it exactly (handles mid-typing like "4316." or "4316.50")
    const dotIdx = cleaned.indexOf('.');
    if (dotIdx >= 0) {
      const intPart = Math.trunc(n);
      const decimalStr = cleaned.slice(dotIdx); // e.g. "." or ".5" or ".50"
      return intPart.toLocaleString('en-IN') + decimalStr;
    }
    return Math.round(n).toLocaleString('en-IN');
  }
  return parseFloat(n.toFixed(4)).toString();
};

/**
 * Strip commas from a typed input value so the underlying numeric state
 * stays clean. Pass the raw `e.target.value` straight through.
 */
export const stripCommas = (v) =>
  typeof v === 'string' ? v.replace(/,/g, '') : v;

/**
 * Convenience: parse a possibly-commified user input back to a Number,
 * or null if empty/invalid. Use before sending to the backend.
 */
export const parseINR = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const cleaned = stripCommas(v);
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
};
